import {rgbToHex, hexToHsv, getRgbLuminance} from '@teemill/utilities';
import {resizeImage, fastResampleHermite} from '@teemill/common/helpers';
import {SierraCanvas, Vector} from '@teemill/common/classes';
import {OmniObject, OmniControl} from './';
import OmnisFilters from '../omnicanvas.filters';

import {
  cropControlLayer,
  defaultControlLayer,
  perspectiveControlLayer,
} from './controlLayers';

import {clamp} from 'lodash';

export default class OmniImage extends OmniObject {
  mipMapCanvas = null;
  className = 'Image';

  constructor(opts) {
    super();
    if (opts) {
      Object.keys(opts).forEach(i => {
        try {
          this[i] = opts[i];
          // eslint-disable-next-line
        } catch (e) {}
      });
    }
  }

  /**
   * This runs before the Image object is initialised and prepares the image for
   * use in the OmniCanvas stage
   *
   * @return {void}
   */
  beforeInit() {
    this.upgradeLegacyFilters();

    if ((this.img_src || this.sourceUrl) && !this.img) {
      this.img = new Image();

      // eslint-disable-next-line
      const element = this;

      this.img.onload = () => {
        element.controller?.render?.();
        element.afterInit();
      };

      this.img.crossOrigin = 'Anonymous';

      this.img.src = this.img_src ? this.img_src : this.sourceUrl;
    } else if (this.img && !this.img.src) {
      if (typeof this.img.toImage === 'function') {
        const cnv = this.img;
        cnv.toImage('image/webp').then(img => {
          cnv.destroy();
          this.changeImage(img);
        });
      }
      // blob or canvas?
      if (typeof this.img.toDataURL === 'function') {
        // canvas
        this.original_img = this.img;
      } else {
        // blob
        const ogCnv = SierraCanvas.global;
        ogCnv
          .resizeCanvas(this.img.width, this.img.height)
          .drawImage(this.img, 0, 0);

        this.original_img = ogCnv.toImage();
      }
    }
    this.mipMaps = [];
  }

  /**
   * This runs after the Image object has been initialised and added to canvas
   * Here, any filters are applied
   *
   * @return {void}
   */
  afterInit() {
    if (!this.img.complete) {
      return;
    }
    this.original_img = this.img;
    this.mipMaps = [];

    if (this.loggedFilters && Object.keys(this.loggedFilters).length) {
      const cachedFilters = this.filters;
      this.filters = this.loggedFilters;

      this.applyFilters();

      this.filters = cachedFilters;
    }

    if (this.filters && Object.keys(this.filters).length) {
      this.applyFilters();
    }
  }

  /**
   * Draws the element onto the canvas
   *
   * @param {Boolean} inclFilters Whether or not to include filters when drawing
   * the element
   */
  draw(inclFilters) {
    // don't draw this element if the image is yet to load, or if the object
    // is of zero width or height
    if (!this.img || !this.img.width || !this.img.height) {
      return;
    }

    this.drawShadow();

    this.ctx.save();

    this.drawImage();

    if (inclFilters && Object.keys(this.filters).length) {
      this.ctx.save();

      this.ctx.globalAlpha *= this.controller?.params?.filterStrength ?? 1;

      this.drawImage();

      this.ctx.restore();
    }

    this.ctx.restore();
  }

  drawImage() {
    this.ctx.save();

    const roundedWidth = Math.round(this.width);
    const roundedHeight = Math.round(this.height);

    let mipMap = this.closestMipMap();

    if (this.perspective) {
      mipMap = this.applyPerspective(mipMap);
    }

    const mipMapRatio = mipMap.width / this.img.width;

    if (this.cropParameters) {
      if (
        this.controlLayer === cropControlLayer &&
        !this.controller?.params?.exportMode
      ) {
        const scale = this.controller?.params?.scale;
        const mipMapCropRatio = mipMap.width / this.cropOriginalWidth;

        this.ctx
          .save()
          .setGlobalAlpha(0.15)
          .translate(
            (this.cropOriginalWidth / 2) * (scale - 1),
            (this.cropOriginalHeight / 2) * (scale - 1)
          )
          .scaleFromPosition(
            scale,
            scale,
            this.cropOriginalWidth / 2,
            this.cropOriginalHeight / 2
          )
          .drawImage(
            mipMap,
            -this.cropParameters.x * (mipMapRatio / mipMapCropRatio),
            -this.cropParameters.y * (mipMapRatio / mipMapCropRatio),
            this.cropOriginalWidth,
            this.cropOriginalHeight
          )
          .restore();
      }

      this.applyRenderScale();

      this.ctx.drawImage(
        mipMap,
        this.cropParameters.x * mipMapRatio,
        this.cropParameters.y * mipMapRatio,
        this.cropParameters.width * mipMapRatio,
        this.cropParameters.height * mipMapRatio,
        0,
        0,
        roundedWidth,
        roundedHeight
      );
    } else {
      this.applyRenderScale();
      this.ctx.drawImage(mipMap, 0, 0, roundedWidth, roundedHeight);
    }

    this.ctx.restore();
  }

  applyRenderScale() {
    const roundedWidth = Math.round(this.width);
    const roundedHeight = Math.round(this.height);

    const scaledWidth = roundedWidth * this.renderScale.x;
    const scaledHeight = roundedHeight * this.renderScale.y;

    const offsetX = (roundedWidth - scaledWidth) / 2;
    const offsetY = (roundedHeight - scaledHeight) / 2;

    this.ctx
      .translate(offsetX, offsetY)
      .scale(this.renderScale.x, this.renderScale.y);
  }

  /**
   * Finds the closest appropriate mip map for the current display size.
   * Mip maps are used to improve rendering performance. Instead of relying on
   * the browser to condense a large image into a smaller area, which results in
   * pixelation and loss of thin lines, we instead render the images to a high
   * quality scaled preview once, and render those images instead. Improves
   * rendering quality and visual fidelity
   *
   * @param {Boolean} forceOriginal If true, ignore the mip maps and instead
   * render the original image (needed when rendering the image to a high
   * quality export)
   * @return {Image} An image or image-like element, such as a canvas
   */
  closestMipMap(forceOriginal) {
    const referenceWidth = this.cropOriginalWidth
      ? this.cropOriginalWidth
      : this.width;
    const scale = referenceWidth / (this.controller?.getPixelScale?.() ?? 1);

    if (!this.mipMapsEnabled || !this.options.mipMapsEnabled) {
      return this.img;
    }

    if (this.controller?.params?.exportMode) {
      const targetScale = referenceWidth / this.img.width;
      if (targetScale < 1) {
        return this.generateMipMap(1 / targetScale, true);
      }
      return this.img;
    }
    if (forceOriginal === true) {
      return this.original_img;
    }

    const mipMaps = this.mipMaps;
    const mipMapScale = clamp(
      (Math.ceil(Math.sqrt(this.img.width / scale)) - 1) * 2,
      0,
      6
    );
    if (mipMapScale === 0) {
      return this.img;
    }
    if (typeof mipMaps[mipMapScale] === 'undefined') {
      return this.generateMipMap(mipMapScale);
    }
    if (mipMaps[mipMapScale] instanceof Image) {
      return mipMaps[mipMapScale];
    }
    return this.img;
  }

  /**
   * Create a scaled, high quality mip map of the Image
   *
   * @param {Number} mipMapScale The relative scale of the mip map. A scale of
   * 2 is half the size of the image. A scale of 4 is one quarter etc.
   * @return {Image} An image or image-like element, such as a canvas
   */
  generateMipMap(mipMapScale, optimiseForPrint) {
    if (!this.mipMapCanvas) {
      this.createMipMapCanvas();
    }
    const targetWidth = this.img.width / mipMapScale;
    const targetHeight = this.img.height / mipMapScale;

    if (optimiseForPrint) {
      this.mipMaps[mipMapScale] = false;

      return resizeImage(
        this.mipMapCanvas.canvas,
        Math.round(targetWidth),
        Math.round(targetHeight)
      );
    }
    this.mipMaps[mipMapScale] = false;

    fastResampleHermite(
      this.mipMapCanvas.canvas,
      Math.round(this.mipMapCanvas.canvas.width),
      Math.round(this.mipMapCanvas.canvas.height),
      Math.round(targetWidth),
      Math.round(targetHeight)
    ).then(img => {
      this.mipMaps[mipMapScale] = img;

      this.originalMipMaps[mipMapScale] = this.mipMaps[mipMapScale];

      // TODO: Make this work with this.applyFilters
      if (this.filters && Object.keys(this.filters).length) {
        OmnisFilters.filterImg(
          this.originalMipMaps[mipMapScale],
          this.filters,
          this.mipMaps[mipMapScale]
        ).then(img => {
          this.mipMaps[mipMapScale] = img;
          if (typeof img.toImageOnIdle === 'function') {
            img.toImageOnIdle().then(convertedImage => {
              if (this.mipMaps[mipMapScale] === img) {
                this.mipMaps[mipMapScale] = convertedImage;
              }
              this.controller.afterNextRender(() => img.destroy());
              this.requestRender();
            });
          }
          this.requestRender();
        });
      }
    });

    if (!(this.mipMaps[mipMapScale] instanceof Image)) {
      return this.img;
    }

    this.originalMipMaps[mipMapScale] = this.mipMaps[mipMapScale];

    // TODO: Make this work with this.applyFilters
    if (this.filters && Object.keys(this.filters).length) {
      OmnisFilters.filterImg(
        this.originalMipMaps[mipMapScale],
        this.filters,
        this.mipMaps[mipMapScale]
      ).then(img => {
        this.mipMaps[mipMapScale] = img;
        if (typeof img.toImageOnIdle === 'function') {
          img.toImageOnIdle(img).then(convertedImage => {
            if (this.mipMaps[mipMapScale] === img) {
              this.mipMaps[mipMapScale] = convertedImage;
            }
            this.controller.afterNextRender(() => img.destroy());
            this.requestRender();
          });
        }
        this.requestRender();
      });
    }

    return this.mipMaps[mipMapScale];
  }

  resetMipMaps() {
    this.originalMipMaps = [];
    this.mipMaps = [];

    if (this.mipMapCanvas) {
      this.mipMapCanvas.destroy();
    }

    this.createMipMapCanvas();
  }

  createMipMapCanvas() {
    this.mipMapCanvas = new SierraCanvas();

    this.mipMapCanvas
      .resizeCanvas(this.img.width, this.img.height)
      .setImageSmoothing(false)
      .drawImage(this.img, 0, 0);
  }

  /**
   * Replace the image in this Image object
   *
   * @param {HTMLImageElement|SierraCanvas} img The new image
   * @param {Boolean} resetFilters If true, any filters on this element will be
   * deleted
   * @return {void}
   */
  changeImage(img, resetFilters) {
    if (typeof img.toImage === 'function') {
      const cnv = img;
      cnv.toImage('image/webp').then(img => {
        cnv.destroy();
        this.changeImage(img);
      });
      return;
    }

    const originalWidth = this.width;
    const originalHeight = this.height;
    this.original_img = img;
    this.img = img;

    this.cropParameters = null;

    if (resetFilters) {
      this.clearFilters();
    } else {
      this.applyFilters();
    }

    this.resetMipMaps();

    if (this.perspective) {
      this._updatePerspectiveCanvas();
    }

    this.controller?.eventBus?.emit('image:changed', this);
    this.controller?.eventBus?.emit(`image-${this.id}:changed`, this);
  }

  /**
   * This converts some older filters to the current format, to ensure compatability when
   * loading older templates
   *
   * Older filters used to be arrays of values - newer filters are either a single numeric value
   * or an object
   */
  upgradeLegacyFilters() {
    // convert modifyHsv from [hsv, mode, strength] to {hsv, mode, strength}
    if (Array.isArray(this.filters.modifyHsv)) {
      const filterArguments = this.filters.modifyHsv;
      this.filters.modifyHsv = {
        hsv: filterArguments[0],
        mode: filterArguments[1],
        strength: filterArguments[2] ?? 1,
      };
    }

    // convert recolour from [rgb, mode, strength] to {rgb, mode, strength}
    if (Array.isArray(this.filters.recolour)) {
      const filterArguments = this.filters.recolour;
      this.filters.recolour = {
        rgb: filterArguments[0],
        mode: filterArguments[1],
        strength: filterArguments[2] ?? 1,
      };
    }

    // convert all single item arrays to a value instead
    Object.keys(this.filters).forEach(filterName => {
      if (
        Array.isArray(this.filters[filterName]) &&
        this.filters[filterName].length === 0
      ) {
        this.filters[filterName] = this.filters[filterName][0];
      }
    });
  }

  /**
   * Apply any filters assigned to this object.
   * @see OmnisFilters (./omnicanvas.filters.js)
   *
   * @return {void}
   */
  applyFilters() {
    if (!this.original_img) {
      this.original_img = this.img;
    }

    if (this.filters && Object.keys(this.filters).length) {
      OmnisFilters.filterImg(this.original_img, this.filters, this.img).then(
        img => {
          this.img = img;
          if (typeof img.toImageOnIdle === 'function') {
            img.toImageOnIdle().then(convertedImage => {
              if (this.img === img) {
                this.img = convertedImage;
              }
              this.controller?.afterNextRender?.(() => img.destroy());
              this.requestRender();

              this.controller?.eventBus?.emit('image:changed', this);
              this.controller?.eventBus?.emit(`image-${this.id}:changed`, this);
            });
          }
          this.requestRender();

          this.controller?.eventBus?.emit('image:changed', this);
          this.controller?.eventBus?.emit(`image-${this.id}:changed`, this);
        }
      );

      if (this.mipMapsEnabled) {
        if (!this.originalMipMaps) {
          this.originalMipMaps = [];
        }
        if (!this.mipMaps) {
          this.mipMaps = [];
        }

        Object.keys(this.mipMaps).forEach(k => {
          if (typeof this.originalMipMaps[k] === 'undefined') {
            this.originalMipMaps[k] = this.mipMaps[k];
          }
          OmnisFilters.filterImg(
            this.originalMipMaps[k],
            this.filters,
            this.mipMaps[k]
          ).then(img => {
            this.mipMaps[k] = img;
            if (typeof img.toImageOnIdle === 'function') {
              img.toImageOnIdle().then(convertedImage => {
                if (this.mipMaps[k] === img) {
                  this.mipMaps[k] = convertedImage;
                }
                this.controller?.afterNextRender?.(() => img.destroy());
                this.requestRender();

                this.controller?.eventBus?.emit('image:changed', this);
                this.controller?.eventBus?.emit(
                  `image-${this.id}:changed`,
                  this
                );
              });
            }
            this.requestRender();
          });
        });
      }
    } else {
      this.clearFilters();
    }

    if (this.perspective) {
      this._updatePerspectiveCanvas();
    }
  }

  /**
   * Attach a filter to this element. The filter will not be applied until
   * applyFilters is applied
   * @see applyFilters
   *
   * @param {String} filterName The name of the filter to apply
   * @param {Any} strength The strength of the filter. The exact data type or
   * ranges of this value vary depending on the filter being applied. See
   * OmnisFilters to confirm what values are expected per filter
   * @see OmnisFilters ('./omnicanvas.factory.js')
   * @return {void}
   */
  addFilter(filterName, args) {
    if (filterName === 'vignette' && !this.vignettable) {
      return;
    }
    this.filters[filterName] = args;
  }

  /**
   * Remove a filter from this element. If not applying immediately,
   * applyFilters must be used after running this function
   *
   * @param {String} filterName The name of the filter to remove
   * @param {Boolean} apply Whether or not to immediately apply these changes
   * @return {void}
   */
  removeFilter(filterName, apply) {
    delete this.filters[filterName];
    if (apply !== false) {
      this.applyFilters();
    }
  }

  /**
   * Remove all filters assigned to this object
   *
   * @return {void}
   */
  clearFilters() {
    this.filters = {};
    this.mipMaps = [];
    this.img = this.original_img;
  }

  /**
   * Add extra postable parameters specific to this class
   *
   * @param {Object} post The default post parameters provided by getPostableParams
   * @see getPostableParams
   * @return {void}
   */
  extendPostableParams(post) {
    post.img_url = this.img.src;
    post.filters = this.filters;
  }

  /**
   * Apply a cookie-cutter style cutout (mask) to this Image element.
   * Essentially what this means is that the alpha channel of the cutout image
   * will be applied to the main image of this element
   *
   * @param {Image} img The image to be applied as a cutout (mask) on this
   * element
   * @return {void}
   */
  addCutout(img) {
    this.cutout = img;
    if (this.cutout.className) {
      this.cutout.init(this.controller);
    }
    this.mipMaps = [];
    this.controller?.render?.();
  }

  /**
   * Returns an object that represents all the differences between this element
   * and the default version of itself
   *
   * @return {Object}
   */
  toDelta() {
    const original = new OmniImage();
    const delta = this.controller?.comparePropertiesInObjects?.(
      this,
      original,
      [
        'img',
        'mipMaps',
        'mipMapCanvas',
        'original_img',
        'originalMipMaps',
        'colourIndex',
        'dominantColour',
        'containsWhite',
        'averageHsv',
        'baseControls',
        'perspectiveMode',
        'perspectiveCanvas',
      ]
    );
    delta.className = this.className;

    this._enforceCleanDeltaPosition(delta);

    this._getDeltaImageSource(delta);

    return delta;
  }

  _getDeltaImageSource(delta) {
    if (!this.sourceUrl) {
      if (this.original_img) {
        if (!this.original_img.src) {
          delta.img_src = this.original_img.toDataURL();
        } else {
          delta.img_src = this.original_img.src;
        }
      }
    }
  }

  /**
   * Gather some information about this image, such as how many colours there
   * are, what the dominant colour is and if this image contains white
   *
   * @param {Number} accuracy How accurate the colour detection needs to be
   * Lower values are more precise. For best results, use between 8 and 64
   * @return {void}
   */
  getImageMetaData(accuracy) {
    if (!accuracy) {
      accuracy = 32;
    }

    let img = this.original_img || this.img;

    if (img.nodeName === 'IMG' && !img.complete) {
      img.onload = () => {
        this.getImageMetaData(accuracy);
      };
      return {};
    }

    if (this.mipMapsEnabled && this.mipMaps) {
      const mipMapKeys = Object.keys(this.mipMaps);
      const smallestMipMap = Math.max.apply(null, mipMapKeys);
      if (!isNaN(smallestMipMap) && this.mipMaps[smallestMipMap]) {
        img = this.mipMaps[smallestMipMap];
      }
    }

    const cnv = SierraCanvas.global;

    // scales the image down so that it has <= 500,000 pixels
    const imgPixelCount = img.width * img.height;
    const scaleRatio = imgPixelCount / 50000;

    cnv
      .resizeCanvas(
        Math.round(img.width / scaleRatio),
        Math.round(img.height / scaleRatio)
      )
      .drawImage(img, 0, 0, cnv.width, cnv.height);

    const data = cnv.getImageData(0, 0, cnv.width, cnv.height).data;

    let hex;
    const allCols = {};
    const colourIndex = [];
    let containsWhite = false;
    // let uniqueColCount = 0;
    let totalPixels = 0;

    for (let i = 0, l = data.length; i < l; i += 4) {
      let a = data[i + 3];

      a = Math.round(a / 50) * 50;
      if (a > 50) {
        const r = Math.round(data[i] / accuracy) * accuracy;
        const g = Math.round(data[i + 1] / accuracy) * accuracy;
        const b = Math.round(data[i + 2] / accuracy) * accuracy;
        hex = rgbToHex({r, g, b});
        const lum = getRgbLuminance({r, g, b});
        if (lum > 0.97) {
          containsWhite = true;
        }

        if (!allCols[hex]) {
          allCols[hex] = 1;
        } else {
          allCols[hex]++;
        }
        totalPixels += 1;
      }
    }
    let dominantColour = false;
    let dominantColPixelCount = 0;
    Object.keys(allCols).forEach(i => {
      if (allCols[i] > totalPixels / 100) {
        // uniqueColCount++;
        colourIndex.push(i);
        if (allCols[i] > dominantColPixelCount) {
          dominantColour = i;
          dominantColPixelCount = allCols[i];
        }
      }
    });

    this.colourIndex = colourIndex;
    this.dominantColour = dominantColour;
    this.containsWhite = containsWhite;

    const hsvAvg = {
      h: 0,
      s: 0,
      v: 0,
    };
    let occurrenceSum = 0;

    for (let i = 0, l = colourIndex.length; i < l; ++i) {
      hex = colourIndex[i];
      const occurrences = allCols[hex];
      const hsv = hexToHsv(hex);
      hsvAvg.h += hsv.h * occurrences;
      hsvAvg.s += hsv.s * occurrences;
      hsvAvg.v += hsv.v * occurrences;
      occurrenceSum += occurrences;
    }
    hsvAvg.h /= occurrenceSum;
    hsvAvg.s /= occurrenceSum;
    hsvAvg.v /= occurrenceSum;

    this.averageHsv = hsvAvg;

    return {
      colourIndex,
      dominantColour,
      hsvAvg,
    };
  }

  toggleCropMode() {
    if (!this.cropParameters) {
      this.cropParameters = {
        x: 0,
        y: 0,
        width: this.img.width,
        height: this.img.height,
      };
    }

    if (!this.cropOriginalWidth || !this.cropOriginalHeight) {
      this.cropOriginalWidth = this.width;
      this.cropOriginalHeight = this.height;
    } else {
      const widthRatio = this.img.width / this.cropParameters.width;
      const heightRatio = this.img.height / this.cropParameters.height;

      this.cropOriginalWidth = this.width * widthRatio;
      this.cropOriginalHeight = this.height * heightRatio;
    }

    if (this.controlLayer !== cropControlLayer) {
      this.enableCropMode();
    } else {
      this.disableCropMode();
    }

    this.controller?.render?.();
  }

  enableCropMode() {
    if (!this.controls.cropTopLeft) {
      this.controls.cropTopLeft = new OmniControl({
        layer: cropControlLayer,
        placement: new Vector(0, 0),
        element: this,
        action(from, to) {
          const delta = from.deltaTo(to).invert();

          this.element.adjustCropParameters(
            delta.x,
            delta.y,
            -delta.x,
            -delta.y
          );

          this.element.controller?.render?.();
        },
        customCursor: 'nw-resize',
        render: ctx => {
          ctx.setShadow(0, 0, 2, 'rgba(0, 0, 0, 1)');
          ctx
            .setStrokeStyle('white')
            .setLineWidth(5)
            .beginPath()
            .moveTo(-2.5, 10)
            .lineTo(-2.5, -2.5)
            .lineTo(10, -2.5)
            .stroke()
            .closePath();
        },
      });

      this.controls.cropTopRight = new OmniControl({
        layer: cropControlLayer,
        placement: new Vector(1, 0),
        element: this,
        action(from, to) {
          const delta = from.deltaTo(to).invert();

          this.element.adjustCropParameters(0, delta.y, delta.x, -delta.y);

          this.element.controller?.render?.();
        },
        customCursor: 'ne-resize',
        render: ctx => {
          ctx.setShadow(0, 0, 2, 'rgba(0, 0, 0, 1)');
          ctx
            .setStrokeStyle('white')
            .setLineWidth(5)
            .beginPath()
            .moveTo(2.5, 10)
            .lineTo(2.5, -2.5)
            .lineTo(-10, -2.5)
            .stroke()
            .closePath();
        },
      });

      this.controls.cropBottomLeft = new OmniControl({
        layer: cropControlLayer,
        placement: new Vector(0, 1),
        element: this,
        action(from, to) {
          const delta = from.deltaTo(to).invert();

          this.element.adjustCropParameters(delta.x, 0, -delta.x, delta.y);

          this.element.controller?.render?.();
        },
        customCursor: 'ne-resize',
        render: ctx => {
          ctx.setShadow(0, 0, 2, 'rgba(0, 0, 0, 1)');
          ctx
            .setStrokeStyle('white')
            .setLineWidth(5)
            .beginPath()
            .moveTo(-2.5, -10)
            .lineTo(-2.5, 2.5)
            .lineTo(10, 2.5)
            .stroke()
            .closePath();
        },
      });

      this.controls.cropBottomRight = new OmniControl({
        layer: cropControlLayer,
        placement: new Vector(1, 1),
        element: this,
        action(from, to) {
          const delta = from.deltaTo(to).invert();

          this.element.adjustCropParameters(0, 0, delta.x, delta.y);

          this.element.controller?.render?.();
        },
        customCursor: 'nw-resize',
        render: ctx => {
          ctx.setShadow(0, 0, 2, 'rgba(0, 0, 0, 1)');
          ctx
            .setStrokeStyle('white')
            .setLineWidth(5)
            .beginPath()
            .moveTo(2.5, -10)
            .lineTo(2.5, 2.5)
            .lineTo(-10, 2.5)
            .stroke()
            .closePath();
        },
      });
    }

    this.setControlLayer(cropControlLayer);
  }

  disableCropMode() {
    if (this.controlLayer === cropControlLayer) {
      this.setControlLayer(defaultControlLayer);
    }
  }

  adjustCropParameters(x, y, width, height) {
    const xScale = this.img.width / this.cropOriginalWidth;
    const yScale = this.img.height / this.cropOriginalHeight;

    const ogCropParameters = Object.assign({}, this.cropParameters);

    if (!this.cropParameters) {
      this.cropParameters = {
        x: 0,
        y: 0,
        width: this.img.width,
        height: this.img.height,
      };
    }

    this.cropParameters.x += x * xScale;
    this.cropParameters.y += y * yScale;

    this.cropParameters.width += width * xScale;
    this.cropParameters.height += height * yScale;

    this.limitCropParameters();

    this.position.x += (this.cropParameters.x - ogCropParameters.x) / xScale;
    this.position.y += (this.cropParameters.y - ogCropParameters.y) / yScale;

    this.width += (this.cropParameters.width - ogCropParameters.width) / xScale;
    this.height +=
      (this.cropParameters.height - ogCropParameters.height) / yScale;
  }

  limitCropParameters() {
    this.cropParameters.x = clamp(this.cropParameters.x, 0, this.img.width);

    this.cropParameters.y = clamp(this.cropParameters.y, 0, this.img.height);

    this.cropParameters.width = clamp(
      this.cropParameters.width,
      0,
      this.img.width - this.cropParameters.x
    );

    this.cropParameters.height = clamp(
      this.cropParameters.height,
      0,
      this.img.height - this.cropParameters.y
    );
  }

  flipHorizontal() {
    const currentXFlip = Math.sign(this.renderScale.x);

    this.berp('renderScale.x', currentXFlip, currentXFlip * -1, 350);
  }

  flipVertical() {
    const currentYFlip = Math.sign(this.renderScale.y);

    this.berp('renderScale.y', currentYFlip, currentYFlip * -1, 350);
  }

  onBeforeDelete() {
    if (this.mipMapCanvas) {
      this.mipMapCanvas.destroy();
    }

    if (
      this.original_img &&
      this.original_img !== this.img &&
      typeof this.original_img.destroy === 'function'
    ) {
      this.original_img.destroy();
    }

    if (this.perspectiveCanvas) {
      this.perspectiveCanvas.destroy();
    }

    // release the renderer canvas from memory
    this.ctx = null;
    this.cnv = null;
  }

  onDeselect() {
    if (this.controlLayer === cropControlLayer) {
      this.toggleCropMode();
    } else if (this.controlLayer === perspectiveControlLayer) {
      this.togglePerspectiveMode();
    }
  }

  /**
   * log filters logs the filters that have been applied from separate sources
   * we can later reapply them if needed
   *
   * @param {Array} filters An array of SierraCanvas compatible filters to apply
   */
  logFilters(filters) {
    filters.forEach(filter => {
      this.loggedFilters[filter.method] = filter.params;
    });
  }

  toBlob(type = 'image/png', quality = 0.92, options = {}) {
    return new Promise(resolve => {
      let targetWidth = this.img.width;
      let targetHeight = this.img.height;

      if (options.maxWidth && options.maxHeight) {
        if (targetWidth > options.maxWidth) {
          targetHeight *= options.maxWidth / targetWidth;
          targetWidth = options.maxWidth;
        }

        if (targetHeight > options.maxHeight) {
          targetWidth *= options.maxHeight / targetHeight;
          targetHeight = options.maxHeight;
        }
      }
      const cnv = new SierraCanvas(targetWidth, targetHeight);

      cnv.drawImage(this.img, 0, 0, targetWidth, targetHeight);

      cnv.toBlob(
        blob => {
          resolve(blob);
          cnv.destroy();
        },
        type,
        quality
      );
    });
  }

  filters = {};
  loggedFilters = {};
  vignettable = false;
  color = false;
  /**
   * @type {HTMLImageElement}
   */
  img = undefined;
  mipMaps = [];
  mipMapsEnabled = false;
  originalMipMaps = [];
  renderScale = new Vector(1, 1);
}
